import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import type { FormField } from '../interfaces';
import type { FormValuesType } from './FormContainer';
import {
    evaluateExpression,
    expressionContainsEmptyOperand,
    findCamundaProperty,
    findCamundaPropertyValue,
    getLocalizedLabel,
} from '../utils';
import { FormikNumericFormatEditable } from './FormFields/FormikNumericFormatEditable';
import type { Theme } from '@material-ui/core';
import { IconButton } from '@material-ui/core';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import classNames from 'classnames';
import { Alert } from '@material-ui/lab';
import { Block } from '@klp/design/Block';
import { Tooltip } from '@klp/design/Tooltip';
import { useTranslation } from 'react-i18next';

const styles = (theme: Theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    },
    floatingButton: {
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        right: theme.spacing(1),
        top: 0,
        bottom: 0,
        '& .EditActive': {
            backgroundColor: theme.palette.secondary.main,
            '& svg': {
                fill: theme.palette.primary.veryLight,
            },
        },
    },
});

type CalculatedSelectorEditableProps = {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
};

type CalculatedEditable = {
    isEdited: boolean;
    value?: number;
};

const parseFormikValue = (formikValue?: string): CalculatedEditable => {
    try {
        return formikValue
            ? JSON.parse(formikValue)
            : { isEdited: false, value: undefined };
    } catch {
        return { isEdited: false, value: undefined };
    }
};

export const CalculatedSelectorEditable: React.FC<
    CalculatedSelectorEditableProps
> = ({ field, isReadOnly, isRequired }) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { setFieldValue, values } = useFormikContext<FormValuesType>();
    const [, meta] = useField(field.id!);

    const calculateExpr = findCamundaPropertyValue(
        'calculate',
        field.properties,
    );
    const hiddenIfEmptyOperand = findCamundaProperty(
        'hiddenIfEmptyOperand',
        field.properties,
    );

    const calculatedValue = useMemo(
        () =>
            calculateExpr
                ? evaluateExpression<number>(calculateExpr, values)
                : undefined,
        [calculateExpr, values],
    );

    const localFormikObject = useMemo(
        () => parseFormikValue(values[field.id!]),
        [values, field.id],
    );
    const [isEditMode, setIsEditMode] = useState(localFormikObject.isEdited);

    useEffect(() => {
        if (calculatedValue === undefined) return;
        if (
            localFormikObject.value === calculatedValue ||
            localFormikObject.isEdited
        ) {
            return;
        }

        setFieldValue(
            field.id,
            JSON.stringify({
                isEdited: false,
                value: calculatedValue,
            }),
        );
    }, [
        calculatedValue,
        field.id,
        isEditMode,
        localFormikObject.isEdited,
        localFormikObject.value,
        setFieldValue,
    ]);

    const handleValueChange = useCallback(
        (value?: number) => {
            if (!isEditMode) return;

            setFieldValue(
                field.id,
                JSON.stringify({
                    isEdited: value !== calculatedValue && isEditMode,
                    value,
                }),
            );
        },
        [isEditMode, calculatedValue, setFieldValue, field.id],
    );

    const handleEditReset = useCallback(() => {
        setIsEditMode(false);
        setFieldValue(
            field.id,
            JSON.stringify({ isEdited: false, value: calculatedValue }),
        );
    }, [calculatedValue, field.id, setFieldValue]);

    const startEditing = useCallback(() => {
        setIsEditMode(true);
    }, []);

    const isHidden =
        hiddenIfEmptyOperand &&
        expressionContainsEmptyOperand(values, calculateExpr);
    if (isHidden) return null;

    return (
        <>
            <div className={classNames(classes.container)}>
                <FormikNumericFormatEditable
                    variant="outlined"
                    id={field.id}
                    placeholder={
                        findCamundaPropertyValue(
                            'placeholder',
                            field.properties,
                        ) || ''
                    }
                    isRequired={isRequired}
                    isReadOnly={!isEditMode || isReadOnly}
                    label={getLocalizedLabel(
                        `project.formFields.${field.id}`,
                        field.label,
                        field.id,
                    )}
                    onFieldEditChange={handleValueChange}
                    value={localFormikObject.value}
                    helperText={meta.touched ? meta.error : ''}
                    error={meta.touched && Boolean(meta.error)}
                />
                {isEditMode && !isReadOnly && (
                    <div className={classes.floatingButton}>
                        <Tooltip translation="general.revert">
                            <IconButton
                                aria-label="reset"
                                onClick={handleEditReset}
                                color="primary"
                            >
                                <Illustration icon={IconName.CLOSE} />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
                {!isEditMode && !isReadOnly && (
                    <div className={classes.floatingButton}>
                        <Tooltip translation="general.edit">
                            <IconButton
                                aria-label="edit"
                                onClick={startEditing}
                                className={classNames('EditActive')}
                                color="secondary"
                            >
                                <Illustration icon={IconName.EDIT} />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
            </div>

            {localFormikObject.isEdited && isEditMode && (
                <Block marginTop="tiny">
                    <Alert
                        severity="info"
                        data-testid="CalculatedSelectorEditable__Alert"
                    >
                        {t(
                            'project.formFields.calculatedSelectorEditable.editedLabel',
                            { value: calculatedValue },
                        )}
                    </Alert>
                </Block>
            )}
        </>
    );
};
